<template>
  <div class="flex flex-col max-h-[88vh] w-full bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 font-sans">
    <!-- HEADER LIMPO NATIVO -->
    <div class="pb-3 px-6 pt-5">
      <h3 class="text-base font-semibold text-slate-900 dark:text-slate-100">
        {{ mode === 'edit' ? 'Editar Permissão' : 'Adicionar Permissão' }}
      </h3>
      <p class="text-xs text-slate-500 dark:text-slate-400 mt-1">
        Permissões permitem que você crie funções com permissões e níveis de acesso específicos para atender aos requisitos da organização.
      </p>
    </div>

    <!-- FORMULÁRIO COM DESIGN NATIVO CLEAN -->
    <form @submit.prevent="handleCustomRole" class="flex flex-col flex-1 overflow-y-auto px-6 py-2 space-y-4">
      <!-- NOME -->
      <div>
        <label class="block text-sm font-medium text-slate-800 dark:text-slate-200 mb-1.5">
          Nome
        </label>
        <input
          ref="nameInput"
          v-model.trim="name"
          type="text"
          required
          placeholder="Por favor, insira um nome."
          class="w-full h-10 px-3 text-sm bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:border-[#1F93FF] focus:bg-white dark:focus:bg-slate-800 transition-colors"
        />
      </div>

      <!-- DESCRIÇÃO -->
      <div>
        <label class="block text-sm font-medium text-slate-800 dark:text-slate-200 mb-1.5">
          Descrição
        </label>
        <textarea
          v-model.trim="description"
          rows="3"
          placeholder="Por favor, insira uma descrição."
          class="w-full p-3 text-sm bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:border-[#1F93FF] focus:bg-white dark:focus:bg-slate-800 transition-colors resize-none"
        />
      </div>

      <!-- ISOLAMENTO POR TIMES (TOGGLE NATIVO) -->
      <div class="flex items-center justify-between py-2 border-t border-b border-slate-100 dark:border-slate-800">
        <div>
          <span class="block text-sm font-medium text-slate-800 dark:text-slate-200">
            Isolamento de Acesso por Times
          </span>
          <span class="block text-xs text-slate-500 dark:text-slate-400">
            Restringe o usuário a visualizar apenas conversas, contatos e tarefas do seu time
          </span>
        </div>

        <!-- TOGGLE SWITCH NATIVO -->
        <label class="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            v-model="teamScopedAccess"
            class="sr-only peer"
          />
          <div class="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer dark:bg-slate-700 peer-checked:bg-[#1F93FF] transition-colors relative after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5"></div>
        </label>
      </div>

      <!-- SEÇÃO PERMISSÕES -->
      <div>
        <div class="flex items-center justify-between mb-2">
          <label class="text-sm font-medium text-slate-800 dark:text-slate-200">
            Permissões
          </label>

          <!-- AÇÕES RÁPIDAS SUTIS -->
          <div class="flex items-center gap-3 text-xs text-[#1F93FF]">
            <button
              type="button"
              @click="applyPreset('all')"
              class="hover:underline cursor-pointer"
            >
              Selecionar todas
            </button>
            <span class="text-slate-300">|</span>
            <button
              type="button"
              @click="applyPreset('read_only')"
              class="hover:underline cursor-pointer"
            >
              Apenas leitura
            </button>
            <span class="text-slate-300">|</span>
            <button
              type="button"
              @click="applyPreset('clear')"
              class="text-slate-500 hover:underline cursor-pointer"
            >
              Limpar
            </button>
          </div>
        </div>

        <!-- CAMPO DE BUSCA DISCRETO -->
        <div class="mb-3">
          <input
            v-model="permissionSearch"
            type="text"
            placeholder="Filtrar permissões..."
            class="w-full h-8 px-3 text-xs bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded-md text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:border-[#1F93FF]"
          />
        </div>

        <!-- LISTA NATIVA DE CHECKBOXES AGRUPADOS -->
        <div class="space-y-4 max-h-[36vh] overflow-y-auto pr-1">
          <div
            v-for="cat in filteredCategories"
            :key="cat.key"
            class="space-y-2"
          >
            <!-- CABEÇALHO DE GRUPO SUAVE -->
            <div class="flex items-center justify-between pt-1">
              <span class="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                {{ cat.title }}
              </span>
              <button
                type="button"
                @click="toggleAllInCategory(cat)"
                class="text-[11px] text-[#1F93FF] hover:underline"
              >
                {{ isAllCategorySelected(cat) ? 'Desmarcar' : 'Marcar todos' }}
              </button>
            </div>

            <!-- ITENS CHECKBOX NATIVOS -->
            <div class="space-y-2 pl-0.5">
              <label
                v-for="perm in cat.permissions"
                :key="perm.key"
                class="flex items-center gap-2.5 text-sm font-normal text-slate-800 dark:text-slate-200 hover:text-slate-950 dark:hover:text-white cursor-pointer select-none"
              >
                <input
                  type="checkbox"
                  :value="perm.key"
                  v-model="selectedPermissions"
                  class="size-4 rounded border-slate-300 dark:border-slate-600 text-[#1F93FF] focus:ring-0 focus:ring-offset-0 cursor-pointer"
                />
                <span class="text-sm font-normal">
                  {{ $t(`CUSTOM_ROLE.PERMISSIONS.${perm.key.toUpperCase()}`) }}
                </span>
              </label>
            </div>
          </div>

          <!-- SEÇÃO: OCULTAÇÃO DE MENUS DA BARRA LATERAL -->
          <div class="space-y-2 pt-2 border-t border-slate-100 dark:border-slate-800">
            <span class="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
              Ocultação de Menus da Barra Lateral
            </span>
            <div class="space-y-2 pl-0.5">
              <label
                v-for="item in AVAILABLE_SIDEBAR_MENU_ITEMS"
                :key="item.key"
                class="flex items-center gap-2.5 text-sm font-normal text-slate-800 dark:text-slate-200 hover:text-slate-950 dark:hover:text-white cursor-pointer select-none"
              >
                <input
                  type="checkbox"
                  :checked="hiddenMenuItems.includes(item.key)"
                  @change="toggleHiddenMenu(item.key)"
                  class="size-4 rounded border-slate-300 dark:border-slate-600 text-rose-500 focus:ring-0 focus:ring-offset-0 cursor-pointer"
                />
                <span class="text-sm font-normal">
                  Ocultar aba {{ item.label }}
                </span>
              </label>
            </div>
          </div>
        </div>
      </div>

      <!-- FOOTER NATIVO -->
      <div class="flex items-center justify-end gap-2.5 pt-4 pb-2 border-t border-slate-100 dark:border-slate-800">
        <button
          type="button"
          @click="emit('close')"
          class="px-4 py-2 text-sm font-medium rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors cursor-pointer"
        >
          Cancelar
        </button>
        <button
          type="submit"
          :disabled="isSaving || !name.trim()"
          class="px-5 py-2 text-sm font-medium rounded-lg bg-[#1F93FF] hover:bg-[#1877d2] text-white transition-colors cursor-pointer disabled:opacity-50"
        >
          {{ isSaving ? 'Salvando...' : 'Enviar' }}
        </button>
      </div>
    </form>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import {
  AVAILABLE_CUSTOM_ROLE_PERMISSIONS,
  CUSTOM_ROLE_PERMISSION_CATEGORIES,
  AVAILABLE_SIDEBAR_MENU_ITEMS,
} from 'dashboard/constants/permissions.js';

const props = defineProps({
  mode: {
    type: String,
    default: 'add',
    validator: value => ['add', 'edit'].includes(value),
  },
  selectedRole: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['close']);

const store = useStore();
const name = ref('');
const description = ref('');
const teamScopedAccess = ref(false);
const selectedPermissions = ref([]);
const hiddenMenuItems = ref([]);
const permissionSearch = ref('');
const isSaving = ref(false);
const nameInput = ref(null);

const isAllCategorySelected = (cat) => {
  return cat.permissions.every(p => selectedPermissions.value.includes(p.key));
};

const toggleAllInCategory = (cat) => {
  const allSelected = isAllCategorySelected(cat);
  const catKeys = cat.permissions.map(p => p.key);
  if (allSelected) {
    selectedPermissions.value = selectedPermissions.value.filter(k => !catKeys.includes(k));
  } else {
    selectedPermissions.value = [...new Set([...selectedPermissions.value, ...catKeys])];
  }
};

const toggleHiddenMenu = (key) => {
  if (hiddenMenuItems.value.includes(key)) {
    hiddenMenuItems.value = hiddenMenuItems.value.filter(k => k !== key);
  } else {
    hiddenMenuItems.value.push(key);
  }
};

const filteredCategories = computed(() => {
  const q = permissionSearch.value.toLowerCase().trim();
  if (!q) return CUSTOM_ROLE_PERMISSION_CATEGORIES;

  return CUSTOM_ROLE_PERMISSION_CATEGORIES.map(cat => {
    const matchingPerms = cat.permissions.filter(p => {
      return (
        p.key.toLowerCase().includes(q) ||
        cat.title.toLowerCase().includes(q)
      );
    });
    return {
      ...cat,
      permissions: matchingPerms,
    };
  }).filter(cat => cat.permissions.length > 0);
});

const applyPreset = (type) => {
  if (type === 'all') {
    selectedPermissions.value = [...AVAILABLE_CUSTOM_ROLE_PERMISSIONS];
  } else if (type === 'read_only') {
    const viewPerms = [];
    CUSTOM_ROLE_PERMISSION_CATEGORIES.forEach(cat => {
      cat.permissions.forEach(p => {
        if (p.isView) viewPerms.push(p.key);
      });
    });
    selectedPermissions.value = [...viewPerms];
  } else if (type === 'clear') {
    selectedPermissions.value = [];
  }
};

onMounted(() => {
  if (props.mode === 'edit' && props.selectedRole) {
    name.value = props.selectedRole.name || '';
    description.value = props.selectedRole.description || '';
    teamScopedAccess.value = Boolean(props.selectedRole.team_scoped_access);
    selectedPermissions.value = props.selectedRole.permissions ? [...props.selectedRole.permissions] : [];
    hiddenMenuItems.value = props.selectedRole.hidden_menu_items ? [...props.selectedRole.hidden_menu_items] : [];
  } else {
    // Default preset for new role
    applyPreset('all');
  }
  nameInput.value?.focus();
});

const handleCustomRole = async () => {
  if (!name.value.trim()) return;
  if (selectedPermissions.value.length === 0) {
    useAlert('Selecione pelo menos uma permissão.');
    return;
  }

  isSaving.value = true;
  try {
    const payload = {
      name: name.value.trim(),
      description: description.value.trim(),
      team_scoped_access: teamScopedAccess.value,
      permissions: selectedPermissions.value,
      hidden_menu_items: hiddenMenuItems.value,
    };

    if (props.mode === 'edit') {
      await store.dispatch('customRole/updateCustomRole', {
        id: props.selectedRole.id,
        ...payload,
      });
      useAlert('Função personalizada atualizada com sucesso.');
    } else {
      await store.dispatch('customRole/createCustomRole', payload);
      useAlert('Função personalizada adicionada com sucesso.');
    }

    emit('close');
  } catch (error) {
    console.error('Error saving custom role:', error);
    useAlert(error.message || 'Erro ao salvar função personalizada.');
  } finally {
    isSaving.value = false;
  }
};
</script>
